/**
 * One gateway on the Gateways tab: its name and route number (the containers
 * sending through it are gone since 23/10/2026 — client QA: "Not required"),
 * drops and SMS sent / planned, today's real delivered %, bot % and CR (with how many sent
 * drops have stats behind them), the price per SMS (its own column since
 * 22/10/2026 — user: "add route price as column in table"), the estimated
 * cost, and its status.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { usd } from '@/lib/reporting-currency';

import { Tooltip } from '@/components/ui';

import type { GatewayToday } from '../gateways-today';
import { StatusChip } from './StatusChip';

export const GRID = 'grid grid-cols-[minmax(190px,1.3fr)_88px_110px_84px_72px_72px_80px_80px_112px] items-center gap-3 px-5';

const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(v * 100 >= 10 ? 0 : 1)}%`);
const n = (v: number) => v.toLocaleString('en-US');

const Rate: FC<{ value: number | null; gateway: GatewayToday; what: string; warnAbove?: number; warnBelow?: number }> = ({ value, gateway, what, warnAbove, warnBelow }) => {
  const r = gateway.rates;
  if (!r || r.dropsSent === 0) return <span className="text-right font-mono text-xs tabular-nums text-muted-foreground">—</span>;
  const basis = r.dropsWithStats === 0 ? 'sent, stats not collected yet — Evaluate verdicts on the Live tab reads them' : `${what} over ${r.dropsWithStats} of ${r.dropsSent} sent drop${r.dropsSent === 1 ? '' : 's'} (${n(r.smsSent)} SMS)`;
  const warn = value !== null && ((warnAbove !== undefined && value > warnAbove) || (warnBelow !== undefined && value < warnBelow));
  return (
    <Tooltip content={basis}>
      <span className={`block text-right font-mono text-xs tabular-nums ${warn ? 'font-semibold text-warning' : ''}`}>{pct(value)}</span>
    </Tooltip>
  );
};

export const GatewayRow: FC<{ gateway: GatewayToday }> = ({ gateway: g }) => (
  <div className={`${GRID} border-b border-border/30 py-2 text-sm last:border-b-0`} data-testid="gateway-row">
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-xs font-semibold" title={g.name}>{g.name}</span>
      <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground">#{g.routeId}</span>
    </span>
    <span className="text-right font-mono text-xs tabular-nums">
      {g.sent.drops} <span className="text-muted-foreground">/ {g.planned.drops}</span>
    </span>
    <span className="text-right font-mono text-xs tabular-nums">
      {n(g.sent.sms)} <span className="text-muted-foreground">/ {n(g.planned.sms)}</span>
    </span>
    <Rate value={g.rates?.deliveredRate ?? null} gateway={g} what="delivered" warnBelow={0.85} />
    <Rate value={g.rates?.botRate ?? null} gateway={g} what="bot clicks" warnAbove={0.15} />
    <Rate value={g.rates?.cr ?? null} gateway={g} what="conversions / visits" />
    <span className="text-right font-mono text-xs tabular-nums" data-testid="gateway-price" title={g.pricePerSms !== null ? 'per SMS' : 'no price on this route'}>
      {g.pricePerSms !== null ? usd(g.pricePerSms, 3) : '—'}
    </span>
    <span className="text-right font-mono text-xs tabular-nums text-muted-foreground">{g.estCost !== null && g.planned.drops > 0 ? usd(g.estCost, 0) : '—'}</span>
    <span className="flex justify-end">
      <StatusChip gateway={g} />
    </span>
  </div>
);
